/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    string, shape, bool, arrayOf,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Card from '@material-ui/core/Card';
import ReactMarkdown from 'react-markdown';
import ReactHtmlParser from 'react-html-parser';
import { useSelector } from 'react-redux';
import {
    ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import index from 'mbp-logger';
import GraphqlWineDisclaimer from './GraphqlWineDisclaimer/WineDisclaimerContainer';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: '40px',
        background: theme.palette.white,
        [theme.breakpoints.down('600')]: {
            marginBottom: '0',
        },
    },
    appBar: {
        width: '100%',
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        margin: '0 auto',
        padding: '15px 0',
        background: theme.palette.white,
        borderTop: `2px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        borderBottom: `2px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
    },
    heading: {
        fontSize: '20px',
        textTransform: 'capitalize',
        fontWeight: '500',
        padding: '0px 15px 15px',
    },

    descriptionBlock: {
        height: '100%',
        display: 'inline-block',
        padding: '0 15px',
        fontSize: '15px',
        '& ul': {
            margin: '10px 0px',
            padding: 'revert',
        },
    },
    descriptionTitle: {
        margin: '5px auto',
        fontSize: '18px',
        fontWeight: '500',
        textAlign: 'center',
    },
    subHeading: {
        display: 'flex',
        margin: '15px 0px',
        '& h2': {
            fontSize: '16px',
            margin: '0',
        },
        '& span': {
            margin: '2px 0 0 5px',
        },
    },
    productDescription: {
        display: 'block',
        padding: '0px 15px',
        boxSizing: 'border-box',
    },
    productDetails: {
        display: 'block',
    },
    content: {
        '& ul': {
            margin: 0,
        },
        margin: '15px 0px',
    },
    productDetailsMobile: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    hidden: {
        display: 'none',
    },
    toggleDescription: {
        margin: '0 0 20px 0',
        padding: '0',
        color: theme.palette.cms?.primary || '#5e3987',
        fontSize: '14px',
        fontWeight: '700',
        textDecoration: 'underline',
        border: 'none',
        cursor: 'pointer',
    },
    mobileShortDes: {
        display: 'inline',
        '& p': {
            display: 'inline',
        },
    },
    promoContainer: {
        border: `1px solid ${theme.palette.cms?.promoMessageBorder || '#EDEDED'}`,
        padding: '18px',
        marginBottom: '10px',
        borderRadius: '3px',
        boxShadow: '0px 2px 2px rgba(125, 100, 100, 0.2)',
        opacity: 1,
    },
    promoMessage: {
        fontSize: '13px',
        textAlign: 'center',
        '& p': {
            margin: '0px',
        },
        '& a': {
            textDecoration: 'underline',
            color: theme.palette.cms?.black || '#000',
        },
    },
    pmallProductRoot: {
        border: 'inherit',
        marginBottom: '24px',
        '& $descriptionBlock': {
            padding: '0px',
            '& ul': {
                paddingLeft: '22px',
                '& li': {
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                },
            },
        },
    },
    panelRoot: {
        borderBottom: `1px solid ${theme.palette.cms?.black || '#2F2F2F'}`,
        borderTop: `1px solid ${theme.palette.cms?.black || '#2F2F2F'}`,
        '&.Mui-expanded': {
            margin: '0px',
        },
        '&:before': {
            display: 'none',
        },
    },
    summaryRoot: {
        '&.Mui-expanded': {
            margin: '0px',
            minHeight: '48px',
        },
        '& .MuiExpansionPanelSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            margin: '0px',
            alignItems: 'center',
        },
    },
    label: {
        color: theme.palette.cms?.black || '#2F2F2F',
        fontSize: '1.5rem',
        lineHeight: '2.5rem',
        fontWeight: 700,
        [theme.breakpoints.down('1023.5')]: {
            fontSize: '1.125rem',
            lineHeight: '1.625rem',
        },
    },
}));

const GraphqlProductDetailsUIAlt = ({
    partNumber, productLongDescription, isPassportBundleProduct, presentationFamily, selectedSku, productBrand, productShortDescription, isPromoRestricted, productBaseContent,
}) => {
    const productDetailsDisplay = `${selectedSku.longDescription || ''} ${selectedSku.shortDescription || ''}` || selectedSku.longDescription || '';

    const descriptionDisplay = productShortDescription || selectedSku.shortDescription || productLongDescription;

    const classes = useStyles();
    const isPmallProduct = useSelector(getFeatureFlag('is-pmall-pdp-template-enabled')) && (productBrand === 'personalizationmall' || productBrand === 'thingsremembered');
    const additionalInformation = productBaseContent?.entries?.[0]?.additional_information || [];
    let descriptionContents = {};
    if (additionalInformation && additionalInformation.length > 0) {
        descriptionContents = additionalInformation.find((elem) => (elem.description_contents_heading));
    }

    const [showBundleLongDescription, setShowBundleLongDescription] = useState(false);

    // If Promo Restriction message is not coming from CMS then this default message will be rendered.
    const defaultPromoMessage = 'Promotion Codes are not accepted on this item. Click [here][1] for gifts that can accept Promotion Codes. \n[1]: https://www.1800flowers.com/Flowers-Gifts-10317\n ';
    const restrictionPromoContent = productBaseContent?.entries?.[0]?.promo_restriction?.promo_restriction_message || defaultPromoMessage;
    const [contentActive, setContentActive] = useState(false);
    const handleChangePanel = (panel) => (event, newExpanded) => {
        setContentActive((newExpanded) ? panel : null);
    };
    return (
        <>
            {isPromoRestricted && presentationFamily === 'flower' && (
                <div className={classes.promoContainer}>
                    <div className={classes.promoMessage}>
                        <ReactMarkdown
                            source={restrictionPromoContent}
                            escapeHtml={false}
                        />
                    </div>
                </div>
            )}
            <Card className={isPmallProduct ? `${classes.pmallProductRoot} ${classes.root}` : classes.root} variant="outlined">
                <div className={classes.descriptionBlock}>
                    {descriptionContents && (
                        <div className={classes.descriptionTitle}>
                            {descriptionContents?.description_contents_heading?.content_heading}
                        </div>
                    )}
                    {!isPmallProduct && (
                        <div className={classes.subHeading}>
                            <h2>Product Details</h2>: <span>{partNumber}</span>
                        </div>
                    )}
                    <div>
                        {/**
                     * @intention
                     * - Desktop: Always visible
                     * - Mobile: Hidden by default. Toggle visibility (read more/less)
                     */}
                        <div className={classes.productDetails}>
                            {productDetailsDisplay && (
                                <div className={classes.content}>{
                                    ReactHtmlParser(productDetailsDisplay, {
                                        transform(node) {
                                            if (node?.type === 'tag' && node?.name === 'a') {
                                                const nodesData = node;
                                                const newData = nodesData?.children?.some((getNode) => getNode?.children?.some((getNode1) => getNode1?.data === 'click here'));
                                                if (nodesData && newData) {
                                                    nodesData.attribs['aria-label'] = 'click-here';
                                                }
                                            }
                                        }, // Added aria-label on click here link for accessibility
                                    })
                                }
                                </div>
                            )}
                            {selectedSku?.ageVerifyFlag === 'true' && productBrand === 'harryanddavid' // showing wine disclaimer only for HD brand products
                                && (
                                    <>
                                        <GraphqlWineDisclaimer />
                                    </>
                                )}
                        </div>
                    </div>
                </div>
                {isPmallProduct ? (
                    <ExpansionPanel classes={{ root: classes.panelRoot }} square expanded={contentActive} onChange={handleChangePanel(index)}>
                        <ExpansionPanelSummary classes={{ root: classes.summaryRoot, content: classes.summaryContent }}>
                            <span className={classes.label}> Description </span>
                            <span className={classes.icons}>{(contentActive) ? <RemoveIcon /> : <AddIcon />}</span>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails classes={{ root: classes.detailsRoot }}>
                            {ReactHtmlParser(descriptionDisplay)}
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                ) : (
                    <AppBar
                        className={`${classes.appBar}`}
                        position="static"
                        elevation={0}
                    >
                        <div className={classes.heading}>
                            Description
                        </div>
                        <div className={classes.productDescription}>
                            {ReactHtmlParser(descriptionDisplay)}
                        </div>
                    </AppBar>
                )}
            </Card>
            {isPassportBundleProduct?.details?.item_sku
            && (
                <Card className={classes.root} variant="outlined">
                    <div className={classes.descriptionBlock}>
                        <div className={classes.subHeading}>
                            <h2>Celebrations Passport Details</h2>: <span>{isPassportBundleProduct?.details?.item_sku}</span>
                        </div>
                        <div>
                            {/**
                             * @intention
                             * - Desktop: Always visible
                             * - Mobile: Hidden by default. Toggle visibility (read more/less)
                             */}
                            <div className={classes.longDescription}>
                                <ReactMarkdown source={isPassportBundleProduct?.details?.bundle_details?.long_description} escapeHtml={false} />
                            </div>
                            <div className={showBundleLongDescription ? '' : classes.hidden}><ReactMarkdown source={isPassportBundleProduct?.details?.bundle_details?.long_description} escapeHtml={false} />
                            </div>
                            <div className={classes.productDetailsMobile}>
                                <button
                                    className={classes.toggleDescription}
                                    type="button"
                                    onClick={() => setShowBundleLongDescription(!showBundleLongDescription)}
                                >
                                    {showBundleLongDescription
                                        ? 'Read less'
                                        : 'Read more'}
                                </button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
};

GraphqlProductDetailsUIAlt.defaultProps = {
    isPassportBundleProduct: {},
    selectedSku: {},
    productBrand: '',
};

GraphqlProductDetailsUIAlt.propTypes = {
    partNumber: string.isRequired,
    productLongDescription: string.isRequired,
    productShortDescription: string.isRequired,
    isPassportBundleProduct: shape({
        enable: bool,
        prices: {},
        details: shape({
            bundle_details: shape({
                long_description: string,
            }),
        }),
    }),
    presentationFamily: string.isRequired,
    selectedSku: shape({
        ageVerifyFlag: string,
        shortDescription: string,
        longDescription: string,
    }),
    productBrand: string,
    isPromoRestricted: bool.isRequired,
    productBaseContent: shape({
        entries: arrayOf(shape({
            promo_restriction: shape({
                promo_restriction_message: string,
            }),
            additional_information: arrayOf(shape({
                description_contents_heading: {
                    content_heading: string,
                },
            })),
        })),
    }).isRequired,
};

export default GraphqlProductDetailsUIAlt;
